import { z } from "zod";
// Reuse the canonical Json type + schema from the shared barrel's first module
// so `export *` from index.ts has exactly one `Json` / `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Receipts shared contract (Strangler-Fig migration, manifest-6, receipts slice).
 *
 * Single source of truth for the `receipts` wire shape shared by the web SPA
 * (`src/repositories/receipts.ts`) and the NestJS slice
 * (`apps/api/src/receipts/*`). Shape is **snake_case** so the FE<->Nest
 * transport is a field-for-field swap (no renames), matching the Supabase
 * generated `receipts` Row and the Nest repository `toBaseWire()` output.
 *
 * The web repo `.parse()`s Nest responses against these schemas at the flip
 * boundary, so any backend/frontend drift surfaces at runtime (and the repo
 * falls back to the Supabase path).
 *
 * ⚠️ MONEY-FIELD TOLERANCE (financial slice — halala-exact): PostgREST may
 * serialize `numeric` columns as a JSON number OR a string depending on the
 * column type/driver. supabase-js usually hands back `number`, but to never
 * reject a valid amount (which would silently fall back to Supabase) every
 * monetary field accepts `number | string | null`. The FE keeps its
 * Supabase-inferred `number | null` types via a cast at the call site.
 *
 * ⚠️ RELATION TOLERANCE: the list/detail queries join different column subsets
 * of tenant / property / unit / contract / invoice, any of which may be `null`
 * (nullable FKs). They are loose `.passthrough()` records so a too-strict
 * relation schema can't force a silent fallback. Only the receipt's own
 * identity columns are pinned.
 *
 * BE adoption note: `apps/api` does not yet import this package at runtime —
 * it keeps its own typed wire interfaces (already shape-aligned). Wiring Nest to
 * consume `@dallali/shared` needs the dual ESM/CJS build step tracked in
 * `docs/migration/domain-debugging-matrix.md`.
 */

/** A monetary value as it may arrive over the wire — number, numeric-string, or null. */
const money = z.union([z.number(), z.string()]).nullable();

/** Loose, passthrough relation shape (nullable joined object / optional). */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A persisted receipt row as the staff Finance dashboard consumes it.
 * Own-columns are pinned to the Supabase generated `receipts` Row identity;
 * money + relation fields are tolerant per the rules above. `.passthrough()`
 * lets the server add columns (new migrations) without a same-PR schema bump.
 */
export const receiptSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    receipt_number: z.string().nullable().optional(),
    // Money (halala-exact) — tolerate number|string|null.
    amount: money.optional(),
    // Dates.
    payment_date: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().nullable().optional(),
    deleted_at: z.string().nullable().optional(),
    // Categorical.
    payment_channel: z.string().optional(),
    transaction_type: z.string().optional(),
    // FK columns (nullable).
    invoice_id: z.string().nullable().optional(),
    contract_id: z.string().nullable().optional(),
    tenant_id: z.string().nullable().optional(),
    property_id: z.string().nullable().optional(),
    unit_id: z.string().nullable().optional(),
    // Optional text.
    reference_number: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    document_url: z.string().nullable().optional(),
    // Joined / enriched relations (list + detail) — loose + optional + nullable.
    // list relations:
    tenant: relation,
    property: relation,
    unit: relation,
    contract: relation,
    // detail-only relation:
    invoice: relation,
  })
  // Tolerate extra columns the server may add without forcing a schema bump.
  .passthrough();

export type Receipt = z.infer<typeof receiptSchema>;
